/**
 * ScrollArrows — floating "jump to top / bottom" affordances for a scroll area.
 *
 * Attach the same ref to a scrollable element and to this component:
 *
 *   const ref = useRef<HTMLDivElement>(null);
 *   <div ref={ref} style={{ overflowY: 'auto' }}>…</div>
 *   <ScrollArrows targetRef={ref} />
 *
 * When the target overflows, a down-arrow appears pinned to the bottom edge of
 * the target (click → smooth-scroll to bottom); once scrolled down, an up-arrow
 * appears at the top edge (click → smooth-scroll to top). In the middle both
 * show. Nothing renders when the content fits without scrolling.
 *
 * Positioned via the target's bounding rect and rendered through a portal, so
 * it needs no DOM restructuring and never scrolls away with the content. It
 * recomputes on scroll, on resize (ResizeObserver + window), and on DOM changes
 * inside the target (so collapsing/expanding panels re-evaluates overflow), all
 * funnelled through a single rAF-debounced update.
 */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IcTriUp, IcTriDown } from './icons';

interface Props {
  targetRef: React.RefObject<HTMLElement | null>;
  /** Minimum overflow (px) before arrows appear. Avoids flicker on near-fits. */
  threshold?: number;
}

interface State {
  showUp: boolean;
  showDown: boolean;
  left: number;
  top: number;
  bottom: number;
}

const HIDDEN: State = { showUp: false, showDown: false, left: 0, top: 0, bottom: 0 };

export default function ScrollArrows({ targetRef, threshold = 16 }: Props) {
  const [s, setS] = useState<State>(HIDDEN);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    const compute = () => {
      rafRef.current = undefined;
      const overflow = el.scrollHeight - el.clientHeight;
      if (overflow <= threshold) { setS(HIDDEN); return; }
      const r = el.getBoundingClientRect();
      const atTop = el.scrollTop <= 4;
      const atBottom = el.scrollTop >= overflow - 4;
      setS({
        showUp: !atTop,
        showDown: !atBottom,
        left: r.left + r.width / 2,
        top: r.top,
        bottom: r.bottom,
      });
    };
    // rAF-debounce: collapse a burst of events into one measurement per frame.
    const update = () => {
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(compute);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Catch content height changes (panels collapsing/expanding, async loads).
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    window.addEventListener('resize', update);

    return () => {
      // Reset the rAF handle, not just cancel it — otherwise a re-mount (e.g.
      // React StrictMode's double-invoke in dev) sees a stale non-null handle
      // and update() skips scheduling, so compute() never runs and no arrows
      // ever appear.
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = undefined; }
      el.removeEventListener('scroll', update);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [targetRef, threshold]);

  const scrollTo = (dir: 'top' | 'bottom') => {
    const el = targetRef.current;
    if (!el) return;
    el.scrollTo({ top: dir === 'top' ? 0 : el.scrollHeight, behavior: 'smooth' });
  };

  if (!s.showUp && !s.showDown) return null;

  return ReactDOM.createPortal(
    <>
      {s.showUp && (
        <button
          aria-label="Scroll to top"
          title="Scroll to top"
          onClick={() => scrollTo('top')}
          style={{ ...arrowStyle, left: s.left, top: s.top + 6 }}
        >
          <IcTriUp size={12} />
        </button>
      )}
      {s.showDown && (
        <button
          aria-label="Scroll to bottom"
          title="Scroll to bottom"
          onClick={() => scrollTo('bottom')}
          style={{ ...arrowStyle, left: s.left, top: s.bottom - 28 }}
        >
          <IcTriDown size={12} />
        </button>
      )}
    </>,
    document.body,
  );
}

const arrowStyle: React.CSSProperties = {
  position: 'fixed',
  transform: 'translateX(-50%)',
  zIndex: 50,
  // Circular accent-filled chip with a dark "ink" arrow glyph for contrast.
  width: 26,
  height: 26,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  fontSize: 10,
  lineHeight: 1,
  cursor: 'pointer',
  color: 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))',
  background: 'var(--accent)',
  border: 'none',
  borderRadius: '50%',
  boxShadow: '0 4px 14px rgba(0,0,0,0.5), 0 0 12px oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  pointerEvents: 'auto',
};
